"use client";
import { getTranslation } from "@/i18n/get-translation";
import AngleDown from "@/icons/angleDown.svg";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { useState } from "react";

interface ISectionItem {
  id: string;
  titleKey: string;
  descKey: string;
}

const sections: ISectionItem[] = [
  { id: "1", titleKey: "s1_title", descKey: "s1_desc" },
  { id: "2", titleKey: "s2_title", descKey: "s2_desc" },
  { id: "3", titleKey: "s3_title", descKey: "s3_desc" },
  { id: "4", titleKey: "s4_title", descKey: "s4_desc" },
  { id: "5", titleKey: "s5_title", descKey: "s5_desc" },
  { id: "6", titleKey: "s6_title", descKey: "s6_desc" },
  { id: "7", titleKey: "s7_title", descKey: "s7_desc" },
  { id: "8", titleKey: "s8_title", descKey: "s8_desc" },
  { id: "9", titleKey: "s9_title", descKey: "s9_desc" },
  { id: "10", titleKey: "s10_title", descKey: "s10_desc" },
  { id: "11", titleKey: "s11_title", descKey: "s11_desc" },
  { id: "12", titleKey: "s12_title", descKey: "s12_desc" },
];

interface ITermsConditonContent {
  locale: string;
}

export default function TermsConditonContent({
  locale,
}: ITermsConditonContent) {
  const t = getTranslation(locale, "termsAndConditionsPage");
  const [activeLink, setActiveLink] = useState("1");

  const handleLinkClick = (link: string) => {
    setActiveLink(link);
  };
  return (
    <section className="relative max-w-[100rem] w-full mx-auto flex lg:flex-row flex-col justify-start items-start lg:gap-0 gap-6 xl:px-[10rem] lg:px-20 px-0 lg:pt-6 pb-14">
      <div className="lg:flex hidden lg:flex-[1_1_20%] flex-col justify-start items-start text-left lg:pt-20 pt-0 pr-0">
        <h3 className="xl:text-2xl lg:text-xl text-lg   font-normal text-primary-800 mb-6">
          {t("tableOfContent")}
        </h3>
        <ul className="flex justify-start items-start flex-col text-primary-700 gap-3 font-light xl:text-xl lg:text-lg text-base">
          {sections.map((item) => (
            <li key={`contents_item_${item.id}`} className="w-full">
              <a
                href={`#detail_item_${item.id}`}
                onClick={() => handleLinkClick(item.id)}
                className={`${
                  activeLink === item.id
                    ? "text-secondary-800 font-medium after:absolute after:right-0 after:top-0 after:bottom-0 after:bg-secondary-800 after:w-[3px]"
                    : "text-primary-700 font-light"
                } hover:text-secondary-800 text-primary-700 relative w-full block pr-4`}
              >
                {t(item.titleKey)}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <Disclosure
        as="div"
        className="flex lg:hidden flex-col w-full items-stretch justify-start bg-primary-500 border-none px-7 py-4 text-left focus:outline-none"
      >
        {({ open }) => (
          <>
            <DisclosureButton className="flex w-full items-center justify-between focus:outline-none bg-primary-500">
              <span className="text-xl font-normal text-primary-800">
                {t("tableOfContent")}
              </span>
              <div
                className={`bg-primary-400-25 rounded-full w-10 h-10 flex items-center justify-center text-primary-700 transition ${
                  open && "rotate-180 bg-primary-700 text-white"
                }`}
              >
                <AngleDown className="w-4" />
              </div>
            </DisclosureButton>
            <DisclosurePanel
              as="div"
              className="pt-4 pb-2 text-lg text-gray-800 font-light  "
            >
              <span>
                <ul className="flex justify-start items-start flex-col text-primary-700 gap-3 font-light xl:text-xl lg:text-lg text-base">
                  {sections.map((item) => (
                    <li key={`contents_item_${item.id}`}>
                      <a href={`#detail_item_${item.id}`}>{t(item.titleKey)}</a>
                    </li>
                  ))}
                </ul>
              </span>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
      <div className="lg:flex-[1_1_80%] flex text-left flex-col justify-start items-stretch px-7 lg:pr-0 lg:pl-24 lg:pt-16 pt-6 border-l border-primary-200">
        <div className="flex flex-col justify-start items-start gap-4 mb-8">
          <p className="text-lg text-primary-800 font-light">{t("versionInfo")}</p>
          <p className="text-lg text-primary-800 font-light">{t("governingLaw")}</p>
        </div>
        <div className="flex flex-col justify-start items-start gap-8">
          <div className="flex justify-start items-start flex-col text-primary-700 gap-3 font-light xl:text-xl lg:text-lg text-base">
            {sections.map((item) => (
              <div id={`detail_item_${item.id}`} key={item.id} className="mb-4">
                <h3 className="xl:text-[3rem] lg:text-[2.5rem] md:text-[2.2rem] text-[1.88rem] text-primary-800 font-medium leading-none mb-4 block">
                  {item.id}. {t(item.titleKey)}
                </h3>
                <div
                  className="text-lg text-primary-800 font-light legal-content"
                  dangerouslySetInnerHTML={{ __html: t(item.descKey) }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
